import {
  memo, useCallback, useMemo, useState, type JSX, type MouseEvent,
} from 'react';
import {
  CHART_LABEL_PLACEMENT,
  DISEASE_BURDEN_LOD,
} from '@/constants/PatientDashboard/patientTimeline';
import type {
  IBurdenPlottedMarker,
  IChartLaneLogScale,
  IDiseaseBurdenLine,
} from '@/types/PatientDashboard/PatientTimeline.types';
import { getBurdenSeries } from './DiseaseBurden.util';
import ChartLaneFrame from './ChartLaneFrame';
import {
  buildLabelPositions,
  isWithinChartLaneView,
  yLogPct,
} from './ChartLane.util';
import BurdenSeriesLines from './BurdenSeriesLines';
import BurdenValueMarker from './BurdenValueMarker';

interface IProps {
  lines: IDiseaseBurdenLine[];
  colour: string;
  scale: IChartLaneLogScale;
  xPct: (date: string) => number;
}

function BurdenChart({
  lines, colour, scale, xPct,
}: IProps): JSX.Element {
  const [hoveredSeries, setHoveredSeries] = useState<string | null>(null);
  const { placedMarkers, connected } = useMemo(() => {
    const series = lines.flatMap(getBurdenSeries);

    // Thresholds are laid out first so a reading's glyph always sits above one it overlaps.
    const markers: IBurdenPlottedMarker[] = [
      ...series.flatMap((line) => line.points.map((point) => ({
        key: `${line.key}-${point.id}-lod`,
        assay: line.assay,
        seriesKey: line.key,
        date: point.date,
        pointId: point.id,
        x: xPct(point.date),
        y: yLogPct(point.lod, scale),
        value: point.lod,
        colour: DISEASE_BURDEN_LOD.colour,
        isDetected: point.detected,
        placement: CHART_LABEL_PLACEMENT.BELOW,
        isThreshold: true,
      }))),
      ...series.flatMap((line) => line.points.map((point) => ({
        key: `${line.key}-${point.id}-purity`,
        assay: line.assay,
        seriesKey: line.key,
        date: point.date,
        pointId: point.id,
        x: xPct(point.date),
        y: yLogPct(point.purity, scale),
        value: point.purity,
        colour,
        isDetected: point.detected,
        placement: CHART_LABEL_PLACEMENT.ABOVE,
        isThreshold: false,
      }))),
    ].filter((marker) => isWithinChartLaneView(marker.x));

    return {
      placedMarkers: buildLabelPositions(markers),
      connected: series.filter((line) => line.points.length > 1),
    };
  }, [colour, lines, scale, xPct]);

  const handleSeriesEnter = useCallback((event: MouseEvent<SVGPolylineElement>): void => {
    setHoveredSeries(event.currentTarget.getAttribute('data-series'));
  }, []);

  const clearHoveredSeries = useCallback((): void => setHoveredSeries(null), []);

  const isDimmed = (seriesKey: string): boolean => (
    hoveredSeries !== null && hoveredSeries !== seriesKey
  );

  return (
    <ChartLaneFrame
      scale={scale}
      plot={connected.map(({ key, points }) => (
        <BurdenSeriesLines
          key={key}
          seriesKey={key}
          points={points}
          colour={colour}
          scale={scale}
          xPct={xPct}
          isHovered={hoveredSeries === key}
          isDimmed={isDimmed(key)}
          onSeriesEnter={handleSeriesEnter}
          onSeriesLeave={clearHoveredSeries}
        />
      ))}
    >
      {placedMarkers.map((marker) => (
        <BurdenValueMarker
          key={marker.key}
          marker={marker}
          isSeriesHovered={hoveredSeries === marker.seriesKey}
          isDimmed={isDimmed(marker.seriesKey)}
        />
      ))}
    </ChartLaneFrame>
  );
}

export default memo(BurdenChart);
